import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  Signal,
  signal,
  untracked,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { catchError, EMPTY, filter, map, tap, throwError } from 'rxjs';
import { InboxApi } from '../../../shared/services/api/inbox.api';
import { APP_ROUTES } from '../../../app.routes';
import { MenuItemList } from '../../../shared/models/custom-types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { APP_PATHS } from '../../config/app-paths';
import { ApplicationContextService } from '../../services/application-context.service';
import { HttpResponseService } from '../../services/http-error-response.service';
import { Beneficjent } from '../../../shared/models/beneficjent';
import { DefinicjeParametrow } from '../../../shared/enums/params-definition.enum';
import { DokumentApi } from '../../../shared/services/api/dokument.api';
import { SessionCounterService } from '../../services/session-counter.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [InboxApi],
})
export class SidebarComponent {
  public isMobile = input<boolean>();
  public user = input<Beneficjent | null>();
  protected closeSidebar = output<void>();
  protected pendingCount = signal<number>(0);
  readonly #dokumentApi = inject(DokumentApi);
  readonly #sessionCounterService = inject(SessionCounterService);
  protected readonly applicationContextService = inject(
    ApplicationContextService,
  );
  protected readonly isFirstLogin: Signal<boolean>;
  protected readonly INBOX_ROUTE = APP_ROUTES.POST_INBOX;
  protected readonly organizationContext = computed(
    () => this.applicationContextService.isOrganizationContext(),
  );

  #inboxApi = inject(InboxApi);
  #router = inject(Router);
  #httpResponseService = inject(HttpResponseService);
  #destroyRef = inject(DestroyRef);

  protected readonly menuItems = computed<MenuItemList[]>(() => {
    const items = [
      { label: 'Strona główna', icon: 'pi pi-home', route: '/' },
      {
        label: 'Skrzynka odbiorcza',
        icon: 'pi pi-inbox',
        route: '/post/inbox',
      },
      {
        label: 'Wnioski wysłane',
        icon: 'pi pi-send',
        route: '/post/sent',
      },
      {
        label: 'Utwórz organizację',
        icon: 'pi pi-building',
        route: '/create-organization',
        hidden: this.organizationContext(),
      },
      {
        label: 'Instrukcja użytkownika',
        ariaLabel: 'Instrukcja użytkownika, otwiera się w nowej karcie',
        icon: 'pi pi-book',
        action: () => this.redirectToManualInstruction(),
      },
      {
        label: 'Ustawienia',
        icon: 'pi pi-cog cursor-pointer',
        route: this.#navigateToSettings(),
        mobileOnly: true,
      },
      {
        label: `Wyloguj (${this.user()?.imie} ${this.user()?.nazwisko})`,
        icon: 'pi pi-sign-out',
        mobileOnly: true,
        hidden: !this.user(),
        action: () => {
          this.#sessionCounterService.endSession();
        },
      },
    ];
    return items.filter(
      (item) => !item.hidden && (this.isMobile() || !item.mobileOnly),
    );
  });

  constructor() {
    const isFirstLoginUrl = (url: string) =>
      url.includes('/' + APP_PATHS.auth.firstLogin);

    this.isFirstLogin = toSignal(
      this.#router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => isFirstLoginUrl(e.url)),
      ),
      { initialValue: isFirstLoginUrl(this.#router.url) },
    );

    if (!this.isFirstLogin()) {
      this.#loadInboxCount();

      this.#router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          tap(() => this.#loadInboxCount()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
    effect(() => {
      this.organizationContext();
      untracked(() => {
        if (!this.isFirstLogin() && this.#router.url === '/') {
          this.#loadInboxCount();
        }
      });
    });
  }

  #loadInboxCount(): void {
    const cached = localStorage.getItem('inboxCount');
    if (cached) {
      this.pendingCount.set(+cached);
    }

    this.#inboxApi
      .getAllInboxDocumentsCount()
      .pipe(
        tap((count) => {
          this.pendingCount.set(count);
          localStorage.setItem('inboxCount', String(count));
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania danych w skrzynce odbiorczej',
          );
          this.pendingCount.set(0);

          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #navigateToSettings(): string {
    if (this.organizationContext()) {
      return APP_PATHS.organization.root;
    } else {
      return APP_PATHS.userSettings.root;
    }
  }

  protected redirectToManualInstruction(): void {
    this.#dokumentApi
      .getParamContent$(DefinicjeParametrow.ADRES_INSTRUKCJA)
      .pipe(
        tap((response: string) => {
          if (response) {
            window.open(response, '_blank', 'noopener,noreferrer');
          }
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobrania instrukcji użytkownika',
          );
          return throwError(() => err);
        }),
      )
      .subscribe();
  }
}
